import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import {
  Card,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Tooltip,
  message,
  Popconfirm,
  Spin,
  Alert,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  ExpandAltOutlined,
  CompressOutlined,
  SearchOutlined,
  FolderOutlined,
  FileOutlined,
} from "@ant-design/icons";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import AgGridWithColumnState from "../../components/AgGridWithColumnState";
import {
  getAccountTree,
  createAccount,
  updateAccount,
  deleteAccount,
  getAllAccounts,
  getAccountBalances,
  clearAccountsCache,
} from "../api/accounting";
import { formatCurrencyWithConversion } from "../utils/currencyUtils";
import useDisplayCurrency from "../../../hooks/useDisplayCurrency";
import useGlobalFilters from "../../../hooks/useGlobalFilters";
import {
  getCachedData,
  setCachedData,
  invalidateCacheByPrefix,
  CacheTTL,
} from "../context/AccountingDataCache";

const ChartOfAccounts = ({ selectedBrand }) => {
  // Use global filters (currently unused but kept for future use)
  useGlobalFilters();
  
  // Global currency display - refreshKey changes when currency changes, forcing grid refresh
  const { refreshKey: currencyRefreshKey } = useDisplayCurrency();

  // Create cache key for this brand
  const cacheKey = useMemo(() => {
    if (!selectedBrand) return null;
    return `chart-of-accounts-${selectedBrand.id}`;
  }, [selectedBrand]);

  // Check global cache for initial data
  const initialTreeData = useMemo(() => {
    if (!cacheKey) return [];
    return getCachedData(`${cacheKey}-tree`) || [];
  }, [cacheKey]);

  const [rawTree, setRawTree] = useState(initialTreeData);
  const [loading, setLoading] = useState(initialTreeData.length === 0);
  const [, setExpandedKeys] = useState([]);
  const [searchText, setSearchText] = useState("");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [editing, setEditing] = useState(null);
  const [parentForNew, setParentForNew] = useState(null);
  const [, setParentDepth] = useState(0);
  const [, setAllCategories] = useState([]);
  const [accountBalances, setAccountBalances] = useState({});

  const brandFilter = useMemo(() => {
    return selectedBrand ? { brand: selectedBrand.id } : {};
  }, [selectedBrand]);

  const fetchingRef = useRef(false);
  const lastFetchKeyRef = useRef(initialTreeData.length > 0 ? cacheKey : null);
  const fetchTreeRef = useRef(null);
  const fetchCategoriesRef = useRef(null);
  const fetchBalancesRef = useRef(null);

  const fetchTree = useCallback(
    async (force = false) => {
      if (!selectedBrand || !cacheKey) {
        setRawTree([]);
        return;
      }

      const treeCacheKey = `${cacheKey}-tree`;

      // Prevent duplicate simultaneous requests
      if (fetchingRef.current && !force) {
        return;
      }

      // Check GLOBAL cache first if not forcing refresh
      if (!force) {
        const cachedTree = getCachedData(treeCacheKey);
        if (cachedTree) {
          setRawTree(cachedTree);
          setLoading(false);
          lastFetchKeyRef.current = cacheKey;
          return;
        }
      }

      // If we just fetched this exact same data, skip
      if (!force && lastFetchKeyRef.current === cacheKey) {
        setLoading(false);
        return;
      }

      fetchingRef.current = true;
      lastFetchKeyRef.current = cacheKey;
      setLoading(true);

      try {
        const data = await getAccountTree(brandFilter);
        setRawTree(data || []);

        // Cache in GLOBAL cache (5 minute TTL)
        setCachedData(treeCacheKey, data || [], CacheTTL.MEDIUM);
      } catch (err) {
        console.error(err);
        message.error(
          err?.response?.data?.detail || "Failed to fetch accounts"
        );
      } finally {
        setLoading(false);
        fetchingRef.current = false;
      }
    },
    [selectedBrand, brandFilter, cacheKey]
  );

  // derive categories from existing accounts to avoid hardcoding
  const fetchCategoriesFromAccounts = useCallback(
    async (force = false) => {
      if (!selectedBrand || !cacheKey) {
        setAllCategories([]);
        return;
      }

      const categoriesCacheKey = `${cacheKey}-categories`;

      // Check GLOBAL cache first if not forcing refresh
      if (!force) {
        const cachedCategories = getCachedData(categoriesCacheKey);
        if (cachedCategories) {
          setAllCategories(cachedCategories);
          return;
        }
      }

      try {
        const flat = await getAllAccounts(brandFilter);
        const cats = Array.from(
          new Set(
            (flat?.results || flat || []).map((a) => a.category).filter(Boolean)
          )
        );
        setAllCategories(cats);

        // Cache in GLOBAL cache (5 minute TTL)
        setCachedData(categoriesCacheKey, cats, CacheTTL.MEDIUM);
      } catch {
        // fallback to common categories if API fails
        const fallback = ["asset", "liability", "equity", "income", "expense"];
        setAllCategories(fallback);
        setCachedData(categoriesCacheKey, fallback, CacheTTL.MEDIUM);
      }
    },
    [selectedBrand, brandFilter, cacheKey]
  );

  // Fetch account balances
  const fetchAccountBalances = useCallback(
    async (force = false) => {
      if (!selectedBrand || !cacheKey) {
        setAccountBalances({});
        return;
      }

      const balancesCacheKey = `${cacheKey}-balances`;

      // Check GLOBAL cache first if not forcing refresh
      if (!force) {
        const cachedBalances = getCachedData(balancesCacheKey);
        if (cachedBalances) {
          setAccountBalances(cachedBalances);
          return;
        }
      }

      try {
        const balances = await getAccountBalances(brandFilter);
        setAccountBalances(balances || {});

        // Cache in GLOBAL cache (5 minute TTL)
        setCachedData(balancesCacheKey, balances || {}, CacheTTL.MEDIUM);
      } catch {
        setAccountBalances({});
      }
    },
    [selectedBrand, brandFilter, cacheKey]
  );

  // Keep refs updated
  useEffect(() => {
    fetchTreeRef.current = fetchTree;
    fetchCategoriesRef.current = fetchCategoriesFromAccounts;
    fetchBalancesRef.current = fetchAccountBalances;
  }, [fetchTree, fetchCategoriesFromAccounts, fetchAccountBalances]);

  // Fetch data when brand changes - with debounce
  useEffect(() => {
    if (!selectedBrand || !cacheKey) {
      setRawTree([]);
      setAllCategories([]);
      setAccountBalances({});
      lastFetchKeyRef.current = null;
      return;
    }

    // Check GLOBAL cache first - restore immediately if available
    const treeCacheKey = `${cacheKey}-tree`;
    const categoriesCacheKey = `${cacheKey}-categories`;
    const balancesCacheKey = `${cacheKey}-balances`;
    
    const cachedTree = getCachedData(treeCacheKey);
    const cachedCategories = getCachedData(categoriesCacheKey);
    const cachedBalances = getCachedData(balancesCacheKey);

    if (cachedTree) {
      setRawTree(cachedTree);
      lastFetchKeyRef.current = cacheKey;
    }
    if (cachedCategories) {
      setAllCategories(cachedCategories);
    }
    if (cachedBalances) {
      setAccountBalances(cachedBalances);
    }

    // If all data is cached, no need to fetch
    if (cachedTree && cachedCategories && cachedBalances) {
      setLoading(false);
      return;
    }

    // Small delay to batch multiple rapid changes
    const timeoutId = setTimeout(() => {
      if (!fetchingRef.current) {
        if (!cachedTree && fetchTreeRef.current) fetchTreeRef.current();
        if (!cachedCategories && fetchCategoriesRef.current) fetchCategoriesRef.current();
        if (!cachedBalances && fetchBalancesRef.current) fetchBalancesRef.current();
      }
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [selectedBrand, cacheKey]);

  const expandAll = () => {
    const keys = [];
    const walk = (nodes) =>
      nodes.forEach((n) => {
        keys.push(n.id);
        walk(n.children || []);
      });
    walk(filteredRawTree);
    setExpandedKeys(keys);
  };
  const collapseAll = () => setExpandedKeys([]);

  const openCreateTopLevel = () => {
    setEditing(null);
    setParentForNew(null);
    setParentDepth(0);
    form.resetFields();
    setIsModalOpen(true);
  };

  const onAddChild = useCallback((node, depth) => {
    setEditing(null);
    setParentForNew(node);
    setParentDepth(depth);
    form.resetFields();
    form.setFieldsValue({ category: node.category });
    setIsModalOpen(true);
  }, [form]);

  const onEditNode = useCallback((node, depth) => {
    setEditing(node);
    setParentForNew(null);
    setParentDepth(depth);
    form.resetFields();
    form.setFieldsValue({
      code: node.code || "",
      name: node.name || "",
      category: node.category || undefined,
      parent: node.parentId ?? null,
    });
    setIsModalOpen(true);
  }, [form]);

  const onDeleteNode = useCallback(async (node) => {
    try {
      await deleteAccount(node.id);
      message.success("Account deleted");
      // Clear API cache and component cache to ensure fresh data everywhere
      clearAccountsCache();
      invalidateCacheByPrefix("chart-of-accounts");
      fetchTree(true);
      fetchAccountBalances(true);
      // Dispatch event to notify other components to refresh accounts
      window.dispatchEvent(new CustomEvent("accountsUpdated"));
    } catch (err) {
      console.error(err);
      message.error(err?.response?.data?.detail || "Delete failed");
    }
  }, [fetchTree, fetchAccountBalances]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      if (!selectedBrand) {
        message.error("Please select a brand first");
        return;
      }
      const payload = { ...values };
      if (parentForNew) payload.parent = parentForNew.id;
      // Ensure brand is included in payload
      payload.brand = selectedBrand.id;

      if (editing) {
        const res = await updateAccount(editing.id, payload);
        if (res?.id || res?.success !== false)
          message.success("Account updated");
      } else {
        const res = await createAccount(payload);
        if (res?.id || res?.success !== false)
          message.success("Account created");
      }
      setIsModalOpen(false);
      // Clear API cache and component cache to ensure fresh data everywhere
      clearAccountsCache();
      invalidateCacheByPrefix("chart-of-accounts");
      fetchTree(true);
      fetchAccountBalances(true);
      // Dispatch event to notify other components to refresh accounts
      window.dispatchEvent(new CustomEvent("accountsUpdated"));
    } catch (err) {
      if (err?.errorFields) return; // form validation error
      // Map backend field errors to form
      const data = err?.response?.data;
      if (data && typeof data === "object" && !Array.isArray(data)) {
        if (data.detail && typeof data.detail === "string") {
          message.error(data.detail);
          return;
        }
        if (
          Array.isArray(data.non_field_errors) &&
          data.non_field_errors.length
        ) {
          message.error(data.non_field_errors.join(", "));
          return;
        }
        const fields = Object.entries(data).map(([name, msgs]) => ({
          name,
          errors: Array.isArray(msgs) ? msgs : [String(msgs)],
        }));
        if (fields.length) {
          form.setFields(fields);
          return;
        }
      }
      console.error(err);
      const detail = err?.response?.data?.detail || err?.message;
      message.error(detail || "Save failed");
    }
  };

  // Build table data from raw tree with optional filtering
  const filterRawTree = useCallback(
    (nodes) => {
      if (!searchText) return nodes;
      const term = searchText.toLowerCase();
      const match = (n) =>
        `${n.code || ""} ${n.name || ""} ${n.category || ""}`
          .toLowerCase()
          .includes(term);
      const walk = (list) =>
        list
          .map((n) => ({ ...n, children: walk(n.children || []) }))
          .filter((n) => match(n) || (n.children && n.children.length));
      return walk(nodes);
    },
    [searchText]
  );

  const toTableData = useCallback((nodes, parentName = null, parentId = null, depth = 1) => {
    return (nodes || []).map((n) => ({
      key: n.id,
      id: n.id,
      name: n.name,
      code: n.code,
      category: n.category,
      is_postable: n.is_postable,
      parentName,
      parentId,
      depth,
      children: toTableData(n.children || [], n.name, n.id, depth + 1),
    }));
  }, []);

  const filteredRawTree = useMemo(
    () => filterRawTree(rawTree),
    [rawTree, filterRawTree]
  );
  const tableData = useMemo(() => {
    const data = toTableData(filteredRawTree);
    const result = calculateRunningBalance(data);
    return result;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredRawTree, toTableData, calculateRunningBalance]);

  // Calculate running balance for each account
  const calculateRunningBalance = useCallback(
    (accounts) => {
      // Calculate balances for each account and its children recursively
      const calculateAccountBalance = (account) => {
        let accountBalance = 0;

        // Get balance for this account
        // Try both string and number keys since account.id might be a number or string
        const accountId = account.id;
        const balanceData =
          accountBalances[accountId] ||
          accountBalances[String(accountId)] ||
          accountBalances[Number(accountId)];

        // Calculate children balances recursively first
        let childrenBalance = 0;
        if (account.children && account.children.length > 0) {
          account.children = account.children.map((child) => {
            const childWithBalance = calculateAccountBalance(child);
            childrenBalance += childWithBalance.accountBalance;
            return childWithBalance;
          });
        }

        // Determine the account balance:
        // 1. If account has a direct balance from journal entries, use it
        // 2. If account is postable and has balanceData, use the direct balance
        // 3. If account is non-postable, use sum of children balances (or direct balance if it exists)
        if (balanceData) {
          // Account has journal entries, use the direct balance
          accountBalance = balanceData.balance || 0;
        } else if (account.is_postable) {
          // Postable account without balance data = 0
          accountBalance = 0;
        } else {
          // Non-postable account: use sum of children balances
          accountBalance = childrenBalance;
        }

        return {
          ...account,
          accountBalance: accountBalance,
        };
      };

      // Calculate balances for all accounts
      const accountsWithBalance = accounts.map(calculateAccountBalance);

      return accountsWithBalance;
    },
    [accountBalances]
  );

  const flattenForMoveSelect = useCallback(
    (nodes, excludeId, depth = 1) => {
      const options = [];
      (nodes || []).forEach((n) => {
        if (n.id === excludeId) return;
        const label = `${n.code || ""} ${n.name}`;
        if (!n.is_postable) {
          options.push({ value: n.id, label, depth, category: n.category });
        }
        if (n.children?.length) {
          options.push(
            ...flattenForMoveSelect(n.children, excludeId, depth + 1)
          );
        }
      });
      return options;
    },
    []
  );

  const columns = useMemo(
    () => [
      {
        headerName: "Account Name",
        field: "name",
        sortable: true,
        filter: true,
        cellRenderer: (params) => (
          <Space size={6}>
            {params.data.is_postable ? <FileOutlined /> : <FolderOutlined />}
            <span>{params.value}</span>
          </Space>
        ),
        width: 250,
      },
      {
        headerName: "Account Code",
        field: "code",
        sortable: true,
        filter: true,
        width: 120,
      },
      {
        headerName: "Account Type",
        field: "category",
        sortable: true,
        filter: true,
        cellRenderer: (params) => (
          <span style={{ textTransform: "capitalize" }}>{params.value}</span>
        ),
        width: 150,
      },
      {
        headerName: "Account Balance",
        field: "accountBalance",
        sortable: true,
        filter: "agNumberColumnFilter",
        hide: false,
        suppressMovable: false,
        cellRenderer: (params) => {
          const balance = params.value || 0;
          // Show balance for all accounts (postable and non-postable)
          // Backend now returns USD, but use formatCurrencyWithConversion for consistency
          return formatCurrencyWithConversion(balance, "USD");
        },
        width: 150,
      },
      {
        headerName: "Parent Account",
        field: "parentName",
        sortable: true,
        filter: true,
        width: 200,
      },
      {
        headerName: "Actions",
        field: "actions",
        cellRenderer: (params) => (
          <Space size={4}>
            <Tooltip
              title={
                params.data.depth >= 4
                  ? "Max depth reached (4 levels)"
                  : params.data.is_postable
                  ? "Cannot add child to postable account (postable accounts cannot have children)"
                  : "Add child account"
              }
            >
              <Button
                size="small"
                icon={<PlusOutlined />}
                onClick={() =>
                  onAddChild(
                    {
                      ...params.data,
                      id: params.data.id,
                      category: params.data.category,
                    },
                    params.data.depth
                  )
                }
                disabled={params.data.depth >= 4 || params.data.is_postable}
              />
            </Tooltip>
            <Tooltip title="Edit">
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() =>
                  onEditNode(
                    { ...params.data, id: params.data.id },
                    params.data.depth
                  )
                }
              />
            </Tooltip>
            <Popconfirm
              title="Delete this account?"
              okText="Yes"
              cancelText="No"
              onConfirm={() => onDeleteNode({ id: params.data.id })}
            >
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        ),
        width: 150,
        sortable: false,
        filter: false,
      },
    ],
    [onAddChild, onEditNode, onDeleteNode]
  );

  return (
    <Card
      title="Active Accounts"
      extra={
        <Space>
          <Input
            allowClear
            placeholder="Search accounts..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            prefix={<SearchOutlined />}
            style={{ width: 240 }}
          />
          <Tooltip title="Collapse all">
            <Button icon={<CompressOutlined />} onClick={collapseAll} />
          </Tooltip>
          <Tooltip title="Expand all">
            <Button icon={<ExpandAltOutlined />} onClick={expandAll} />
          </Tooltip>
          <Tooltip title="Refresh">
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchTree}
              loading={loading}
            />
          </Tooltip>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={openCreateTopLevel}
          >
            New
          </Button>
        </Space>
      }
    >
      <Alert
        type="info"
        style={{ marginBottom: 12 }}
        message={
          <div>
            <strong>Hierarchy:</strong> Level 1 = Main Category (non-postable
            group). Level 2-3 = Sub Categories (non-postable groups). Level 4 =
            Postable accounts (used in journal entries). Only Level 4 accounts
            can have journal entries posted to them. Accounts with children
            cannot be postable. To move an account to a different parent,
            click Edit and change the Parent Account field.
          </div>
        }
        showIcon
      />
      <Spin spinning={loading} tip="Loading accounts...">
        <div className="ag-theme-alpine" style={{ height: 600, width: "100%" }}>
          <AgGridWithColumnState
            key={`chart-of-accounts-${currencyRefreshKey}`}
            tableId="chart-of-accounts-v2"
            rowData={tableData}
            columnDefs={columns}
            defaultColDef={{
              resizable: true,
              sortable: true,
              filter: true,
              hide: false,
            }}
            animateRows={true}
            rowSelection="single"
            suppressRowClickSelection={true}
            getRowId={(params) => params.data.id}
            onGridReady={(params) => {
              // Ensure balance columns are visible (works with both old and new AG Grid API)
              setTimeout(() => {
                const api = params.api;
                const columnApi = params.columnApi || api; // Use columnApi if available, otherwise use api

                // Get columns using the appropriate API
                const getAllColumns =
                  columnApi.getAllColumns || columnApi.getColumns || (() => []);
                const getColumn =
                  columnApi.getColumn ||
                  ((colId) => {
                    const cols = getAllColumns();
                    return cols.find((c) => c.getColId() === colId);
                  });
                const setColumnVisible =
                  columnApi.setColumnVisible ||
                  api.setColumnVisible ||
                  (() => {
                    // No-op fallback if setColumnVisible is not available
                  });

                const accountBalanceCol = getColumn("accountBalance");

                if (accountBalanceCol && !accountBalanceCol.isVisible()) {
                  setColumnVisible("accountBalance", true);
                }

                api.sizeColumnsToFit();
              }, 100);
            }}
            context={{
              onAddChild,
              onEditNode,
              onDeleteNode,
            }}
          />
        </div>
      </Spin>

      <Modal
        title={
          editing
            ? "Edit Account"
            : parentForNew
            ? "Add Child Account"
            : "Add Top-Level Account"
        }
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={handleOk}
        okText={editing ? "Save" : "Create"}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="code"
            label="Code"
            rules={[
              { required: true, message: "Please enter an account code" },
            ]}
          >
            <Input placeholder="e.g., 1-1-001" />
          </Form.Item>
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: "Please enter a name" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Category"
            name="category"
            rules={[
              {
                required: !parentForNew,
                message: "Please select category",
              },
            ]}
          >
            <Select
              disabled={!!parentForNew}
              placeholder={
                parentForNew
                  ? `Inherited: ${parentForNew.category}`
                  : "Select main category"
              }
            >
              {(parentForNew
                ? [parentForNew.category]
                : ["Asset", "Liability", "Equity", "Revenue", "Expense"]
              ).map((c) => (
                <Select.Option key={c} value={c}>
                  {c.charAt(0).toUpperCase() + c.slice(1)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          {editing ? (
            <Form.Item name="parent" label="Parent Account">
              <Select
                placeholder="Top Level (no parent)"
                allowClear
                showSearch
                optionFilterProp="label"
              >
                {flattenForMoveSelect(filteredRawTree, editing?.id)
                  .filter(
                    (opt) =>
                      !editing?.category ||
                      opt.category === editing.category
                  )
                  .map((opt) => (
                    <Select.Option
                      key={opt.value}
                      value={opt.value}
                      label={opt.label}
                    >
                      {"—".repeat(Math.max(0, opt.depth - 1))} {opt.label}
                    </Select.Option>
                  ))}
              </Select>
            </Form.Item>
          ) : null}
          {editing &&
          Array.isArray(editing.children) &&
          editing.children.length > 0 ? (
            <Alert
              type="warning"
              message="This account has children. Changing the category may affect child accounts."
              showIcon
              style={{ marginBottom: 12 }}
            />
          ) : null}
        </Form>
      </Modal>

    </Card>
  );
};

export default ChartOfAccounts;
